import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { catalogs, type Key, type Namespace } from './catalogs'
import { DEFAULT_LOCALE, isRtlLocale, type Locale } from './locales'

interface I18nContextValue {
  readonly locale: Locale
  readonly dir: 'rtl' | 'ltr'
  setLocale(locale: Locale): void
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

/**
 * Locale state lives here for the session. `locale` is a **cloud-owned setting**
 * (device-cache.md: `User.locale` via `PATCH /users/me`), and this provider renders the current
 * choice; the write is the *caller's*, at the one place the language can be changed (`App`'s
 * drawer row). Sets `<html lang dir>` as a side effect, matching the prototype's `app.js` boot
 * behavior.
 *
 * **There is deliberately no `toggleLocale()` here.** There was one, it set React state and
 * nothing else, and it was the whole of the language control for as long as this file said
 * wiring the profile write was somebody else's job. `AuthProvider` reconciles the account's
 * locale on every boot — *"a server change wins"* — so a purely local toggle is not merely
 * unsaved, it is actively undone: switch to English, relaunch, and you are back in Hebrew with
 * no error and nothing to notice. A local-only flip is therefore not a smaller version of the
 * feature, it is a false one, and the primitive that offered it is gone rather than left in
 * reach. Anything changing the locale calls `setLocale` with a value the **server has already
 * accepted**.
 */
export function I18nProvider({
  children,
  initialLocale = DEFAULT_LOCALE,
}: {
  readonly children: ReactNode
  readonly initialLocale?: Locale
}): JSX.Element {
  const [locale, setLocale] = useState<Locale>(initialLocale)
  const dir = isRtlLocale(locale) ? 'rtl' : 'ltr'

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = dir
  }, [locale, dir])

  const value = useMemo<I18nContextValue>(() => ({ locale, dir, setLocale }), [locale, dir])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useLocale(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (ctx === undefined) throw new Error('useLocale() called outside <I18nProvider>')
  return ctx
}

/**
 * Reads one namespace of the current locale's catalog. `t()` throws on an unregistered key —
 * a typo in a call site should fail loudly in development rather than render a raw key to a
 * resident, the same discipline the prototype's `shipped()` applies to stage flags.
 */
export function useTranslation<N extends Namespace>(
  namespace: N,
): {
  readonly t: (key: Key<N>, vars?: Readonly<Record<string, string | number>>) => string
  readonly locale: Locale
} {
  const { locale } = useLocale()
  const catalog = catalogs[locale][namespace] as Readonly<Record<string, string>>

  function t(key: Key<N>, vars?: Readonly<Record<string, string | number>>): string {
    const template = catalog[key]
    if (template === undefined) {
      throw new Error(`Missing i18n key "${String(key)}" in namespace "${String(namespace)}" (${locale})`)
    }
    if (vars === undefined) return template
    return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
      name in vars ? String(vars[name]) : match,
    )
  }

  return { t, locale }
}
